import { type ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IconCamera } from "@tabler/icons-react";
import {
  reloadCurrentUser,
  type CurrentUser,
} from "../../../../domains/user/hooks/useCurrentUser";
import {
  ACCOUNT_PAGE,
  openDesktopSettings,
  leaveDesktopSettingsPage,
} from "../../../../domains/app/stores/desktopSettingsStore";
import {
  apiClient,
  revalidateApiQuery,
  useApiQuery,
} from "../../../../services/api";
import { saveAvatarPick } from "../../../../domains/user/saveAvatarPick";
import { compressAvatarImage } from "../../../../domains/user/utils/compressAvatarImage";
import { defaultAvatarImages } from "../../../../domains/user/utils/pickUserImage";
import {
  useAvatarPicker,
  setPickedAvatar,
  resetAvatarPicker,
} from "../../../../domains/user/stores/avatarPickerStore";
import { displayToast } from "../../../../components/app/AppToast";
import { getInitials } from "../../../../components/ProfilePicture";
import { Avatar } from "../../../../components/ui/Avatar";
import { Button } from "../../../../components/ui/Button";
import Loader from "../../../../components/Loader";
import { SettingsPage, SettingsSection } from "../components/SettingsPanel";
import { AccountSection } from "./AccountSection";

/**
 * Asking for the verified badge.
 *
 * Certification needs a REAL photo: a default avatar does not count, which is
 * why the request button stays inert until the account carries an uploaded one
 * or one has just been picked here.
 */
export function CertificationSection({
  currentUser,
}: {
  currentUser: CurrentUser;
}): React.JSX.Element {
  const { t } = useTranslation();
  const pick = useAvatarPicker();
  // useForm only for a submit-in-flight flag, without owning `useState`.
  const { handleSubmit, formState } = useForm();
  // The same eligibility the Account page reads before offering this page. The
  // page is also reachable by URL (the legacy email redirect lands here), and
  // the API only refuses a request that is still waiting: an approved member
  // would otherwise file a second one and notify the team again.
  const certifiedQuery = useApiQuery(
    "/webapp/private/lemon-certified/request",
    currentUser.lemonCertified !== true ? { durable: true } : false
  );

  const title = t("Lemon certified request");
  const description = t(
    "Verified members show a badge on their profile and their contributions."
  );

  const back = {
    label: t("Account"),
    onBack: () => {
      openDesktopSettings(ACCOUNT_PAGE);
    },
  };

  // Nothing left to ask: the Account page is where this one came from.
  if (
    currentUser.lemonCertified === true ||
    (certifiedQuery.state === "success" && certifiedQuery.body !== null)
  ) {
    return <AccountSection currentUser={currentUser} editing={null} />;
  }

  if (certifiedQuery.state === "pending") {
    return (
      <SettingsPage back={back}>
        <SettingsSection title={title} description={description}>
          <Loader expanded />
        </SettingsSection>
      </SettingsPage>
    );
  }

  if (certifiedQuery.state === "error") {
    return (
      <SettingsPage back={back}>
        <SettingsSection title={title} description={description}>
          <p className="text-sm text-ds-strawberry-600">
            {t("Something went wrong. Please try again.")}
          </p>
        </SettingsSection>
      </SettingsPage>
    );
  }

  const picture = currentUser.profilePicture;
  const customUrl =
    picture !== null && picture.image !== null && picture.image.url !== ""
      ? picture.image.url
      : "";
  const defaultUrl =
    picture !== null && picture.name !== null
      ? defaultAvatarImages[picture.name].default
      : "";
  // This panel never offers removal, so a `removed` pick left over from the
  // avatar dialog means "no custom photo" here, exactly like none at all.
  const pickedDataUrl = pick.status === "picked" ? pick.dataUrl : null;
  const displayedUrl =
    pickedDataUrl !== null
      ? pickedDataUrl
      : customUrl !== ""
        ? customUrl
        : defaultUrl;
  const canSubmit = customUrl !== "" || pickedDataUrl !== null;

  function handleFile(event: ChangeEvent<HTMLInputElement>): void {
    const files = event.target.files;
    if (files === null) return;
    const file = files.item(0);
    event.target.value = "";
    if (file === null) return;
    void compressAvatarImage(file).then(setPickedAvatar, () => {
      displayToast(t("Something went wrong. Please try again."), "error");
    });
  }

  async function submit(): Promise<void> {
    if (pickedDataUrl !== null) {
      const uploaded = await saveAvatarPick({
        status: "picked",
        dataUrl: pickedDataUrl,
      });
      if (uploaded === "failed") {
        displayToast(t("Something went wrong. Please try again."), "error");
        return;
      }
      await reloadCurrentUser();
    }
    try {
      await apiClient.post("/webapp/private/lemon-certified/request");
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    displayToast(t("Certification request sent"), "success");
    resetAvatarPicker();
    void revalidateApiQuery("/webapp/private/lemon-certified/request", {
      durable: true,
    });
    // After an await: the reader may have moved on meanwhile.
    leaveDesktopSettingsPage({ page: "certification" }, ACCOUNT_PAGE);
  }

  return (
    <SettingsPage back={back}>
      <SettingsSection title={title} description={description}>
        <form
          className="flex flex-col items-center gap-4 py-8"
          onSubmit={handleSubmit(submit)}
        >
          {displayedUrl !== "" ? (
            <Avatar
              variant="image"
              src={displayedUrl}
              alt=""
              imageFit="cover"
              size="xl"
              badge={null}
              className="rounded-full border-2 border-ds-lemon-400"
            />
          ) : (
            <Avatar
              variant="initials"
              initials={getInitials(currentUser.fullname)}
              size="xl"
              badge={null}
              className="rounded-full border-2 border-ds-lemon-400"
            />
          )}
          <span className="flex flex-col items-center gap-1 text-center">
            <span className="text-heading-xs font-semibold text-ds-slate-950">
              {t("Upload profile picture")}
            </span>
            <span className="text-desktop-meta text-ds-slate-500">
              {t(
                "A clear photo of your face. We check it against your professional identity."
              )}
            </span>
          </span>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleFile}
                aria-label={t("Choose another picture")}
                className="absolute inset-0 size-full cursor-pointer opacity-0"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                leftIcon={<IconCamera size={16} />}
              >
                {displayedUrl !== ""
                  ? t("Choose another picture")
                  : t("Upload profile picture")}
              </Button>
            </div>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!canSubmit || formState.isSubmitting}
            >
              {t("Request Certification")}
            </Button>
          </div>
        </form>
      </SettingsSection>
    </SettingsPage>
  );
}
